import { Controller } from "@hotwired/stimulus"

/**
 * Controls the mobile slide-out navigation menu.
 *
 * Features:
 * - Opens/closes sidebar drawer on mobile
 * - Shows/hides overlay when menu is open
 * - Prevents body scroll when menu is open
 * - Closes on escape key press
 * - Closes when clicking overlay
 * - Updates ARIA attributes for accessibility
 *
 * Usage:
 *   <body data-controller="mobile-menu">
 *     <div id="mobile-menu-overlay" data-mobile-menu-target="overlay"
 *          data-action="click->mobile-menu#close"></div>
 *     <button id="app_sidebar_mobile_toggle"
 *             data-action="click->mobile-menu#toggle"
 *             aria-expanded="false"
 *             aria-controls="app_sidebar">Menu</button>
 *     <nav id="app_sidebar" data-mobile-menu-target="sidebar">...</nav>
 *   </body>
 */
export default class extends Controller {
  static targets = ["overlay", "sidebar"]

  declare readonly overlayTarget: HTMLElement
  declare readonly sidebarTarget: HTMLElement
  declare readonly hasOverlayTarget: boolean
  declare readonly hasSidebarTarget: boolean

  private boundHandleKeydown: ((e: KeyboardEvent) => void) | null = null
  private toggleButton: HTMLElement | null = null
  private focusableElements: HTMLElement[] = []
  private firstFocusableElement: HTMLElement | null = null
  private lastFocusableElement: HTMLElement | null = null

  connect() {
    this.boundHandleKeydown = this.handleKeydown.bind(this)
    document.addEventListener("keydown", this.boundHandleKeydown)

    // Cache the toggle button reference
    this.toggleButton = document.getElementById("app_sidebar_mobile_toggle")
  }

  disconnect() {
    if (this.boundHandleKeydown) {
      document.removeEventListener("keydown", this.boundHandleKeydown)
    }
  }

  toggle() {
    if (this.isOpen()) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    // Add class to body for global state
    document.body.classList.add("drawer-on")

    // Show the sidebar by adding the drawer-on class
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.add("drawer-on")
    }

    // Show overlay
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("show")
    }

    // Prevent body scroll
    document.body.style.overflow = "hidden"

    // Update ARIA state
    this.updateAriaState(true)

    // Set up focus trap and move focus to sidebar
    this.setupFocusTrap()
    this.focusFirstElement()
  }

  close() {
    // Remove class from body
    document.body.classList.remove("drawer-on")

    // Hide the sidebar
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove("drawer-on")
    }

    // Hide overlay
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("show")
    }

    // Restore body scroll
    document.body.style.overflow = ""

    // Update ARIA state
    this.updateAriaState(false)

    // Return focus to toggle button
    this.returnFocusToToggle()
  }

  private isOpen(): boolean {
    return document.body.classList.contains("drawer-on")
  }

  private handleKeydown(event: KeyboardEvent) {
    if (event.key === "Escape" && this.isOpen()) {
      this.close()
      return
    }

    // Handle focus trap when menu is open
    if (event.key === "Tab" && this.isOpen()) {
      this.handleTabKey(event)
    }
  }

  private updateAriaState(isOpen: boolean) {
    if (this.toggleButton) {
      this.toggleButton.setAttribute("aria-expanded", isOpen.toString())

      // Manage focus for accessibility
      if (isOpen && this.hasSidebarTarget) {
        this.sidebarTarget.focus()
      } else if (!isOpen) {
        this.toggleButton.focus()
      }
    }
  }

  private setupFocusTrap() {
    if (!this.hasSidebarTarget) return

    const focusableSelectors = [
      "button:not([disabled])",
      "[href]",
      "input:not([disabled])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(", ")

    this.focusableElements = Array.from(
      this.sidebarTarget.querySelectorAll<HTMLElement>(focusableSelectors)
    ).filter((el) => el.offsetParent !== null) // Only visible elements

    this.firstFocusableElement = this.focusableElements[0] || null
    this.lastFocusableElement =
      this.focusableElements[this.focusableElements.length - 1] || null
  }

  private handleTabKey(event: KeyboardEvent) {
    if (!this.firstFocusableElement || !this.lastFocusableElement) return

    if (event.shiftKey) {
      // Shift + Tab: if on first element, wrap to last
      if (document.activeElement === this.firstFocusableElement) {
        event.preventDefault()
        this.lastFocusableElement.focus()
      }
    } else {
      // Tab: if on last element, wrap to first
      if (document.activeElement === this.lastFocusableElement) {
        event.preventDefault()
        this.firstFocusableElement.focus()
      }
    }
  }

  private focusFirstElement() {
    // Use requestAnimationFrame to ensure DOM is ready
    requestAnimationFrame(() => {
      if (this.firstFocusableElement) {
        this.firstFocusableElement.focus()
      }
    })
  }

  private returnFocusToToggle() {
    if (this.toggleButton) {
      this.toggleButton.focus()
    }
  }
}
